import React from 'react';
import { RecipePreparationStep } from '../types';
import AuthorizedApiImage from './AuthorizedApiImage';

type Props = {
  value: RecipePreparationStep[];
  onChange: (steps: RecipePreparationStep[]) => void;
  onUploadPhoto?: (index: number, file: File) => Promise<string>;
  canUploadPhoto?: boolean;
};

const fieldStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
  padding: '10px 12px',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-sm)',
  background: 'var(--crm-input-bg)',
  color: 'var(--crm-text)',
  fontSize: 16,
};

export default function RecipePreparationStepsEditor({ value, onChange, onUploadPhoto, canUploadPhoto = false }: Props) {
  const [uploadingStep, setUploadingStep] = React.useState<number | null>(null);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const update = (index: number, changes: Partial<RecipePreparationStep>) => {
    onChange(value.map((step, currentIndex) => currentIndex === index ? { ...step, ...changes } : step));
  };
  const move = (index: number, offset: -1 | 1) => {
    const destination = index + offset;
    if (destination < 0 || destination >= value.length) return;
    const next = [...value];
    [next[index], next[destination]] = [next[destination], next[index]];
    onChange(next);
  };

  return (
    <section aria-label="Кроки приготування" style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong>Кроки приготування</strong>
        <button
          type="button"
          disabled={value.length >= 50}
          onClick={() => onChange([...value, { title: '', instructions: '', estimatedMinutes: null }])}
          style={{ minHeight: 44, padding: '8px 12px', cursor: value.length >= 50 ? 'not-allowed' : 'pointer' }}
        >
          Додати крок
        </button>
      </div>
      {value.length === 0 && (
        <p style={{ margin: 0, color: 'var(--crm-text-muted)' }}>
          Додайте послідовні інструкції. Орієнтовний час є довідковим і не означає активний час роботи.
        </p>
      )}
      {value.map((step, index) => (
        <fieldset key={index} style={{ display: 'grid', gap: 10, minWidth: 0, margin: 0, padding: 12, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)' }}>
          <legend>Крок {index + 1}</legend>
          <label style={{ display: 'grid', gap: 5 }}>
            Назва кроку
            <input
              required
              maxLength={120}
              style={fieldStyle}
              value={step.title}
              onChange={event => update(index, { title: event.target.value })}
              placeholder="Наприклад: Підготувати овочі"
            />
          </label>
          <label style={{ display: 'grid', gap: 5 }}>
            Інструкція
            <textarea
              required
              maxLength={3000}
              rows={3}
              style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.5 }}
              value={step.instructions}
              onChange={event => update(index, { instructions: event.target.value })}
              placeholder="Опишіть конкретні дії та ознаки готовності"
            />
          </label>
          <label style={{ display: 'grid', gap: 5, maxWidth: 220 }}>
            Орієнтовний час, хв
            <input
              type="number"
              min={0}
              max={1440}
              step={1}
              style={fieldStyle}
              value={step.estimatedMinutes ?? ''}
              onChange={event => update(index, { estimatedMinutes: event.target.value === '' ? null : Number(event.target.value) })}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" disabled={index === 0} onClick={() => move(index, -1)} style={{ minHeight: 44, padding: '8px 12px' }}>
              Перемістити вище
            </button>
            <button type="button" disabled={index === value.length - 1} onClick={() => move(index, 1)} style={{ minHeight: 44, padding: '8px 12px' }}>
              Перемістити нижче
            </button>
          </div>
          {step.photoUrl && (
            <AuthorizedApiImage
              src={step.photoUrl}
              alt={`Фото: ${step.title}`}
              style={{ width: '100%', maxWidth: 360, maxHeight: 240, objectFit: 'contain', borderRadius: 'var(--crm-radius-sm)' }}
            />
          )}
          {onUploadPhoto && (
            <label style={{ display: 'grid', gap: 5 }}>
              Фото кроку
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
                disabled={!canUploadPhoto || uploadingStep === index}
                onChange={async event => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  setUploadingStep(index);
                  setUploadError(null);
                  try {
                    const photoUrl = await onUploadPhoto(index, file);
                    update(index, { photoUrl });
                  } catch (error) {
                    setUploadError(error instanceof Error ? error.message : 'Не вдалося завантажити фото кроку.');
                  } finally {
                    setUploadingStep(null);
                  }
                }}
              />
              {!canUploadPhoto && <small>Спочатку збережіть рецепт, щоб додати фото до кроку.</small>}
              {uploadingStep === index && <small>Завантаження фото…</small>}
            </label>
          )}
          <button
            type="button"
            onClick={() => onChange(value.filter((_, currentIndex) => currentIndex !== index))}
            style={{ justifySelf: 'start', minHeight: 44, padding: '8px 12px', color: 'var(--crm-danger)', cursor: 'pointer' }}
          >
            Видалити крок {index + 1}
          </button>
        </fieldset>
      ))}
      {uploadError && <p role="alert" style={{ color: 'var(--crm-danger)' }}>{uploadError}</p>}
    </section>
  );
}
